/**
 * MODULE: core/auth/frontend/utils/persistSignupSession.ts
 * PURPOSE: Signup onboarding state — payment gate before tenant/MFA (pay-first registration).
 * RECENT: 2026-06-13 [2.2.1383] — saveSignupPendingState, payment proof, bypass voucher helpers for signup gate.
 */

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TENANT_ID_KEY = 'tenant_id';
const SIGNUP_WELCOME_KEY = 'brazn_signup_welcome_email';
const SIGNUP_PENDING_KEY = 'brazn_signup_pending';
const SIGNUP_PAYMENT_KEY = 'brazn_signup_payment';
const SIGNUP_BYPASS_KEY = 'brazn_signup_bypass_voucher';

export interface SignupSessionPayload {
  access_token: string;
  refresh_token: string;
  tenant_id?: string;
}

export interface SignupPendingState {
  userId: string;
  email: string;
  tenantResolution?: {
    action?: string;
    existing_tenant?: { id: string; name: string; domain: string; logo_url?: string };
  };
}

export interface SignupPaymentProof {
  checkoutSessionId: string;
  verifiedAt: string;
}

/** Persist in-progress signup after POST /register (before payment). */
export function saveSignupPendingState(state: SignupPendingState): void {
  sessionStorage.setItem(SIGNUP_PENDING_KEY, JSON.stringify(state));
}

export function loadSignupPendingState(): SignupPendingState | null {
  const raw = sessionStorage.getItem(SIGNUP_PENDING_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SignupPendingState;
  } catch {
    return null;
  }
}

export function clearSignupPendingState(): void {
  sessionStorage.removeItem(SIGNUP_PENDING_KEY);
  sessionStorage.removeItem(SIGNUP_PAYMENT_KEY);
  sessionStorage.removeItem(SIGNUP_BYPASS_KEY);
}

/** Stripe checkout session verified on payment-confirm (server-checked). */
export function markSignupPaymentComplete(checkoutSessionId: string): void {
  const proof: SignupPaymentProof = {
    checkoutSessionId,
    verifiedAt: new Date().toISOString(),
  };
  sessionStorage.setItem(SIGNUP_PAYMENT_KEY, JSON.stringify(proof));
}

export function getSignupPaymentProof(): SignupPaymentProof | null {
  const raw = sessionStorage.getItem(SIGNUP_PAYMENT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SignupPaymentProof;
  } catch {
    return null;
  }
}

export function isSignupPaymentComplete(): boolean {
  return Boolean(getSignupPaymentProof()?.checkoutSessionId);
}

/** Tester bypass voucher validated before tenant exists. */
export function saveSignupBypassVoucher(code: string): void {
  sessionStorage.setItem(SIGNUP_BYPASS_KEY, code.trim());
}

export function getSignupBypassVoucher(): string | null {
  const code = sessionStorage.getItem(SIGNUP_BYPASS_KEY);
  return code?.trim() || null;
}

export function isSignupAccessGranted(): boolean {
  return isSignupPaymentComplete() || Boolean(getSignupBypassVoucher());
}

/** Mark that a welcome email was dispatched after complete-registration (shown once on MFA setup). */
export function markSignupWelcomeEmailPending(email: string): void {
  sessionStorage.setItem(SIGNUP_WELCOME_KEY, email.trim());
}

/** Read and clear pending welcome email hint for onboarding UX. */
export function consumeSignupWelcomeEmailPending(): string | null {
  const email = sessionStorage.getItem(SIGNUP_WELCOME_KEY);
  if (email) {
    sessionStorage.removeItem(SIGNUP_WELCOME_KEY);
  }
  return email;
}

/** Persist tokens from POST /complete-registration so AuthProvider can hydrate on next load. */
export function persistSignupSession(payload: SignupSessionPayload): void {
  localStorage.setItem(TOKEN_KEY, payload.access_token);
  localStorage.setItem(REFRESH_TOKEN_KEY, payload.refresh_token);
  if (payload.tenant_id) {
    localStorage.setItem(TENANT_ID_KEY, payload.tenant_id);
  }
}

/** Full navigation so AuthProvider re-initializes from stored tokens. */
export function persistSignupSessionAndRedirect(
  payload: SignupSessionPayload,
  path: string
): void {
  persistSignupSession(payload);
  window.location.assign(path);
}
